<template>
  <section class="sheet__details">
    <SpinnerLoader v-if="props.loading" :size="32" />
    <div v-else-if="props.error" class="sheet__error">
      <BaseText :color="NebraskaColors.elementPrimary" size="p5">
        Não foi possível carregar os dados neste momento. Aguarde alguns
        instantes e tente novamente.
      </BaseText>
      <div @click="emit('retry')">
        <BaseText
          :color="NebraskaColors.elementPrimary"
          size="p5"
          class="sheet__retry"
        >
          Tentar novamente
        </BaseText>
      </div>
    </div>
    <div v-else class="sheet__item-wrapper">
      <div v-for="(section, index) in details" :key="index" class="sheet__item">
        <Heading class="sheet__item-title" :level="3">
          {{ section.title }}
        </Heading>
        <div
          v-for="(item, detailIndex) in section.items"
          :key="detailIndex"
          class="sheet__line"
        >
          <BaseText :color="NebraskaColors.elementSecondary" size="p4">
            {{ item.label }}
          </BaseText>
          <BaseText
            v-if="item.description"
            :color="NebraskaColors.elementSecondary"
            size="p4"
          >
            {{ item.description }}
          </BaseText>
          <a
            v-if="item.link"
            :href="item.link"
            target="_blank"
            class="sheet__link"
          >
            <BaseText
              :color="NebraskaColors.elementPrimary"
              size="p4"
              class="sheet__value"
            >
              {{ item.value }}
            </BaseText>
            <BaseIcon icon="EA0080" width="16" height="16" />
          </a>
          <BaseText
            v-else
            :color="NebraskaColors.elementPrimary"
            size="p4"
            class="sheet__value"
          >
            {{ item.value }}
          </BaseText>
        </div>
        <BaseText
          v-for="(description, indexDescription) in section.description"
          :key="indexDescription"
          :color="NebraskaColors.elementSecondary"
          size="p3"
          :class="{ sheet__description: indexDescription > 0 }"
        >
          {{ description }}
        </BaseText>
        <div v-if="section.message" class="sheet__message">
          {{ section.message }}
        </div>
        <ProductSideSheetSpreadDetails
          v-if="section.title === COSTS_SECTION_TITLE && costs"
          :costs="costs"
        />
      </div>
    </div>
  </section>
</template>

<script setup lang="ts">
import {
  IProductCostsDetails,
  IProductDetailsSection
} from '@/modules/catalog/services/products';
import { NebraskaColors } from '@warrenbrasil/nebraska-tokens-web';
import {
  BaseIcon,
  BaseText,
  Heading,
  SpinnerLoader
} from '@warrenbrasil/nebraska-web';
import { PropType } from 'vue';
import ProductSideSheetSpreadDetails from '../ProductSideSheetSpreadDetails/ProductSideSheetSpreadDetails.vue';

const props = defineProps({
  details: {
    type: Array as PropType<IProductDetailsSection[]>,
    default: null
  },
  costs: {
    type: Object as PropType<IProductCostsDetails>,
    default: null
  },
  loading: Boolean,
  error: Boolean
});

const COSTS_SECTION_TITLE = 'Custos';

const emit = defineEmits(['retry']);
</script>

<style lang="less" scoped>
.sheet {
  &__details {
    width: 100%;
    background: @background-secondary;
    padding: @size-spacing-x1000 @size-spacing-x1200;
    display: flex;
    justify-content: center;
    align-items: center;
    flex-direction: column;
    gap: @size-spacing-x1000;
    height: 100%;
    flex-grow: 1;
    overflow: hidden;
  }

  &__error {
    display: flex;
    justify-content: center;
    flex-direction: column;
    align-items: center;
    text-align: center;
    max-width: 320px;
    gap: @size-spacing-x600;
  }

  &__item-wrapper {
    width: 100%;
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: @size-spacing-x1000;
  }

  &__item {
    width: 100%;
    display: flex;
    flex-direction: column;
    align-items: flex-start;
  }

  &__item-title {
    color: @element-primary;
    margin-bottom: @size-spacing-x500;
    margin-top: 0;
  }

  &__line {
    display: flex;
    justify-content: space-between;
    padding: @size-spacing-x450 0;
    border-top: @size-border-x400 solid @divider-primary;
    width: 100%;

    &:first-of-type {
      padding-top: 0;
      border-top: none;
    }
  }

  &__link {
    display: flex;
    gap: @size-spacing-x150;
    cursor: pointer;
    align-items: center;
  }

  &__description {
    margin-top: @size-spacing-x450;
  }

  &__message {
    color: @element-secondary;
    font-size: 15px;
    background: @background-tertiary;
    padding: @size-spacing-x500;
    margin-top: @size-spacing-x450;
    border-radius: @size-radius-x300;
    width: 100%;
  }

  &__retry {
    &:hover {
      cursor: pointer;
      color: @element-secondary !important;
    }
  }
}

:deep(.initial-avatar-icon) {
  background: @background-secondary;
}
</style>
